import { LocalizedText } from "shared/localizedText";
import {
  DurationIcon,
  DurationUnit,
  Group,
  HealthFormType,
  MarketCode,
  TemplateDto,
  TemplateKey,
  TemplateSustainExerciseDto,
  TemplateUpsertBody,
} from "routes/createContent/api/templateDtos";
import { exerciseImage } from "routes/createContent/utils/exerciseHelpers";
import { DEFAULT_REPS, DEFAULT_SETS } from "./constants";

// Editor-facing form model. It flattens the v2 payload for editing: a
// per-exercise start/end week (converted to the `weeks[]` array on save) and an
// `ongoing` toggle (converted to `duration_weeks: null`). Non-edited display
// fields (title/image/max_level) are carried along so rows can render without a
// refetch and so the shape round-trips through react-hook-form.

export type TemplateFormExercise = {
  exercise_id: number;
  title: string;
  image_url: string | null;
  max_level: number;
  start_week: number;
  end_week: number | null; // null → runs to the end of the program (ongoing)
  recommended_level: number;
  level_locked: boolean;
  pinned: boolean;
  sets: number | null;
  reps: string | null;
  duration_icon: DurationIcon | null;
  duration_unit: DurationUnit | null;
};

export type TemplateFormLesson = {
  text_lesson_id: number;
  title: string;
  image_url: string | null;
};

export type TemplateFormData = {
  id?: number;
  name: LocalizedText;
  group: Group | "";
  key: TemplateKey | null;
  ongoing: boolean; // true → duration_weeks null
  duration_weeks: number; // used when !ongoing
  exercises_per_day: number;
  health_form_type: HealthFormType;
  includes_functional_test: boolean;
  markets: MarketCode[];
  exercises: TemplateFormExercise[];
  lessons: TemplateFormLesson[];
  sustain_exercises: TemplateSustainExerciseDto[];
};

export const emptyTemplate = (): TemplateFormData => ({
  name: {},
  group: "",
  key: null,
  ongoing: false,
  duration_weeks: 12,
  exercises_per_day: 2,
  health_form_type: "generic",
  includes_functional_test: false,
  markets: [],
  exercises: [],
  lessons: [],
  sustain_exercises: [],
});

const rangeInclusive = (start: number, end: number): number[] => {
  const lo = Math.max(1, Math.min(start, end));
  const hi = Math.max(start, end);
  const weeks: number[] = [];
  for (let w = lo; w <= hi; w++) weeks.push(w);
  return weeks;
};

const lastWeek = (weeks: number[]) => (weeks.length ? Math.max(...weeks) : null);

const programEnd = (dto: TemplateDto) => dto.duration_weeks ?? Math.max(1, ...dto.exercises.flatMap(ex => ex.weeks));

export const templateToForm = (dto: TemplateDto): TemplateFormData => ({
  id: dto.id,
  name: dto.name ?? {},
  group: dto.group ?? "",
  key: dto.key,
  ongoing: dto.duration_weeks == null,
  duration_weeks: dto.duration_weeks ?? 12,
  exercises_per_day: dto.exercises_per_day,
  health_form_type: dto.health_form_type,
  includes_functional_test: dto.includes_functional_test,
  markets: dto.markets ?? [],
  exercises: dto.exercises.map(ex => ({
    exercise_id: ex.exercise_id,
    title: ex.title,
    image_url: exerciseImage(ex),
    max_level: ex.max_level,
    start_week: ex.weeks.length ? Math.min(...ex.weeks) : 1,
    end_week: lastWeek(ex.weeks) === programEnd(dto) ? null : lastWeek(ex.weeks),
    recommended_level: ex.recommended_level,
    level_locked: ex.level_locked,
    pinned: ex.pinned,
    sets: ex.recommended_sets,
    reps: ex.recommended_reps,
    duration_icon: ex.duration_icon,
    duration_unit: ex.duration_unit,
  })),
  lessons: [...dto.lessons]
    .sort((a, b) => a.position - b.position)
    .map(ls => ({
      text_lesson_id: ls.text_lesson_id,
      title: ls.title,
      image_url: ls.image_url,
    })),
  sustain_exercises: dto.sustain_exercises ?? [],
});

// A library template must carry both a group and a key, so an incomplete form
// has no body to send.
export const formToUpsertBody = (form: TemplateFormData): TemplateUpsertBody | null => {
  if (!form.group || !form.key) {
    return null;
  }

  const durationWeeks = form.ongoing ? null : form.duration_weeks;
  // Horizon used to expand an exercise's "ongoing" end (no explicit end week)
  // into a concrete weeks[] array. When the whole program is ongoing we have no
  // horizon, so such an exercise just occupies its start week.
  const horizon = durationWeeks ?? Math.max(1, ...form.exercises.map(ex => ex.end_week ?? ex.start_week));

  return {
    name: form.name,
    group: form.group,
    key: form.key,
    duration_weeks: durationWeeks,
    exercises_per_day: form.exercises_per_day,
    health_form_type: form.health_form_type,
    includes_functional_test: form.includes_functional_test,
    markets: form.markets,
    exercises: form.exercises.map(ex => {
      const end = ex.end_week ?? horizon ?? ex.start_week;
      return {
        exercise_id: ex.exercise_id,
        weeks: rangeInclusive(ex.start_week, end),
        days: null,
        level_locked: ex.level_locked,
        pinned: ex.pinned,
        recommended_level: ex.recommended_level,
        recommended_reps: ex.reps,
        recommended_sets: ex.sets,
        duration_icon: ex.duration_icon,
        duration_unit: ex.duration_unit,
      };
    }),
    lessons: form.lessons.map((ls, i) => ({ position: i + 1, text_lesson_id: ls.text_lesson_id })),
    sustain_exercises: form.sustain_exercises,
  };
};

export const buildFormExerciseFromLibrary = (ex: {
  exercise_id: number;
  levels?: Array<{
    title?: string | null;
    image_url?: string | null;
    video_thumbnail_file_url?: string | null;
    sets?: number | null;
    reps?: string | null;
    duration_icon?: DurationIcon | null;
    duration_unit?: DurationUnit | null;
  }> | null;
}): TemplateFormExercise => {
  const level0 = ex.levels?.[0] ?? {};
  // Fixed defaults for a newly-added exercise: Start week 1, End week Ongoing,
  // Level 1, unlocked, Sets 1, Repetitions 15.
  return {
    exercise_id: ex.exercise_id,
    title: level0.title ?? "",
    image_url: exerciseImage(level0),
    max_level: ex.levels?.length ?? 1,
    start_week: 1,
    end_week: null,
    recommended_level: 1,
    level_locked: false,
    pinned: false,
    sets: DEFAULT_SETS,
    reps: DEFAULT_REPS,
    duration_icon: level0.duration_icon ?? "repetitions",
    duration_unit: level0.duration_unit ?? "repetitions",
  };
};

export const buildFormLessonFromLibrary = (ls: {
  id: number;
  title?: string | null;
  image_url?: string | null;
}): TemplateFormLesson => ({
  text_lesson_id: ls.id,
  title: ls.title ?? "",
  image_url: ls.image_url ?? null,
});
